import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import SeoHead from "@/components/common/SeoHead";
import { getImageUrl } from "@/lib/api";

export default function NewsDetail() {
  const { slug } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get(`/news/${slug}`)
      .then((r) => setNews(r.data))
      .catch(() => setNews(null))
      .finally(() => setLoading(false));
    api.get("/news", { params: { limit: 3 } })
      .then((r) => setRelated((r.data.items || []).filter((n) => n.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  if (loading) return <div className="container-sf py-40 text-center text-slate-500">Memuat...</div>;
  if (!news) return (
    <div className="container-sf py-40 text-center">
      <h1 className="text-3xl font-bold">Berita tidak ditemukan</h1>
      <Link to="/berita" className="btn-primary mt-6">Semua Berita</Link>
    </div>
  );

  return (
    <div data-testid="page-news-detail">
      <SeoHead
        title={news.title}
        description={news.excerpt}
        image={news.cover_image}
        type="article"
      />
      <article className="pt-14 pb-16 md:pt-24">
        <div className="container-sm">
          <Link to="/berita" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[var(--brand-blue)] mb-8">
            <ArrowLeft className="h-4 w-4" /> Kembali ke Berita
          </Link>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-orange)] mb-4">{news.category}</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-slate-900">{news.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><User className="h-4 w-4" /> {news.author}</span>
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {new Date(news.published_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</span>
            {news.tags?.length > 0 && (
              <span className="flex items-center gap-2"><Tag className="h-4 w-4" /> {news.tags.join(", ")}</span>
            )}
          </div>
        </div>

        {news.cover_image && (
          <div className="container-sf mt-10">
            <div className="aspect-[16/8] rounded-3xl overflow-hidden bg-slate-100">
              <img src={getImageUrl(news.cover_image)} alt={news.title} className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        <div className="container-sm mt-12 prose prose-slate max-w-none prose-headings:font-bold prose-a:text-[var(--brand-blue)] prose-img:rounded-2xl">
          <p className="text-xl font-serif-display italic text-slate-700 leading-relaxed mb-8">{news.excerpt}</p>
          <div className="text-lg text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: news.content || "" }} />
        </div>
      </article>

      {related.length > 0 && (
        <section className="section-y bg-[var(--bg)]">
          <div className="container-sf">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Berita terkait</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((n) => (
                <Link key={n.id} to={`/berita/${n.slug}`} className="card-sf overflow-hidden group">
                  <div className="aspect-[4/2.5] overflow-hidden bg-slate-100">
                    <img src={getImageUrl(n.cover_image || "https://images.unsplash.com/photo-1747778148316-4a8eeb132d48?w=900&q=80")} alt={n.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{n.category}</div>
                    <h4 className="mt-1 text-lg font-bold text-slate-900 leading-tight line-clamp-2 group-hover:text-[var(--brand-blue)]">{n.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
